import React, { useEffect, useState, useRef } from 'react'
import { isEmpty } from 'lodash'
import moment from 'moment'
import { connect } from 'react-redux'
import { Checkbox } from 'react-bootstrap'
import {
  updateSubmissionWithEdgeNumber,
  updateSubmissionWithEdgeNumberReset
} from '../actions'
import { Loading } from '../../components/shared'
import client from '../../../apollo/client'
import gql from 'graphql-tag'
import { updateRelatedEntitiesWithEdgeNumber } from '../../utils/submission'
import { getSubmission } from '../api'
import { getCurrentSession } from '../../../lib/authentication'

const LinkEdgeNumberModal = (props) => {
  const {
    submission,
    broker,
    updateSubmissionWithEdgeNumber,
    updateSubmissionWithEdgeNumberReset,
    refetchSubmissions,
    user,
    onHide,
    updateEdgeNumberIsBusy,
    updateEdgeNumberFailure
  } = props
  const prevPropsRef = useRef()
  const prevEdgeNumberIsBusyRef = useRef()
  const [input, setInput] = useState('')
  const [invalidNumber, setInvalidNumber] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [saveLoading, setSaveLoading] = useState(false)
  const [alreadyUsedNumber, setAlreadyUsedNumber] = useState(false)
  const [doNotMapChecked, setDoNotMapChecked] = useState(false)
  const [mapCheckLoader, setMapCheckLoader] = useState(false)
  const [noSuchSubmission, setNoSuchSubmission] = useState(false)
  const [submissionUnlinked, setSubmissionUnlinked] = useState(false)
  const [mappedSubmission, setMappedSubmission] = useState({})
  const [existingSubDetail, setExistingSubDetail] = useState(false)
  const [showLoading, setShowLoading] = useState(true)

  useEffect(() => {
    let edgeInputNumber = ''
    let isUnMappable = false

    if (submission?.relatedEntities && submission?.relatedEntities.identifiers) {
      const identifiers = submission?.relatedEntities.identifiers.filter(
        (element) => element?.type === 'edge',
      )
      if (identifiers.length > 0) {
        if (identifiers[0].id != 'N/A') {
          edgeInputNumber = identifiers[0].id
        } else {
          isUnMappable = true
        }
      }
    }

    if (submission?.id != prevPropsRef.current) {
      setInvalidNumber(false)
      setInput(edgeInputNumber)
      setSaveSuccess(false)
      setAlreadyUsedNumber(false)
      if (isUnMappable) {
        setDoNotMapChecked(true)
        return;
      }
      setDoNotMapChecked(false)
    }

    return () => {
      prevPropsRef.current = submission?.id
    }
  }, [submission])

  useEffect(() => {
    if (submission === null || broker === null) {
      setShowLoading(true)
    } else {
      setShowLoading(false)
    }
  }, [submission, broker])

  /**
   * Logic to run after the edge number is updated by the updateSubmissionWithEdgeNumber
   * saga. Basically close the modal popup if the update is successful.
   */
  useEffect(() => {
    if (prevEdgeNumberIsBusyRef.current === true && updateEdgeNumberIsBusy === false) {
      if (saveLoading === true) {
        setSaveLoading(false)
        if (updateEdgeNumberFailure !== true) {
          updateSubmissionWithEdgeNumberReset()
          onHide()
        } else {
          setSaveSuccess(false)
        }

      } else if (mapCheckLoader === true) {
        setMapCheckLoader(false)
        if (doNotMapChecked && updateEdgeNumberFailure !== true) {
          updateSubmissionWithEdgeNumberReset()
          onHide()
        } else if (updateEdgeNumberFailure === true) {
          setSubmissionUnlinked(false)
        }
      }
    }
    return () => {
      prevEdgeNumberIsBusyRef.current = updateEdgeNumberIsBusy
    }
  }, [updateEdgeNumberIsBusy, saveLoading, mapCheckLoader, updateEdgeNumberFailure, doNotMapChecked])

  const updateEdgeNumber = (submission, edgeNumber) => {
    const relatedEntities = updateRelatedEntitiesWithEdgeNumber(
      submission?.relatedEntities,
      edgeNumber,
    )
    updateSubmissionWithEdgeNumber(submission?.id, { relatedEntities }, user, refetchSubmissions, edgeNumber)
  }

  const handleChange = (event) => {
    const value = event.target.value
    if (/^\d{0,15}$/.test(value)) {
      setInput(value)
    }
  }

  const handleSave = async (event) => {
    event.preventDefault()
    const edgeNumber = input
    setSaveLoading(true)
    setSaveSuccess(false)
    setInvalidNumber(false)
    setAlreadyUsedNumber(false)
    setSubmissionUnlinked(false)
    setNoSuchSubmission(false)

    try {
      const { data: { submissionForEdgeNumber } } = await getSubmissionForEdgeNumber(edgeNumber);
      if (submissionForEdgeNumber) {
        if (submissionForEdgeNumber.id === submission.id) {
          updateEdgeNumber(submission, edgeNumber)
          return
        }
        let response
        Promise.all([getSubmission(user, { id: submissionForEdgeNumber.id }), getCurrentSession()])
          .then((results) => {
            let accessToken;
            [response, { accessToken }] = results
            const apiClient = apigClientFactory.newClient()
            return apiClient.apiGetBrokerIdGet(
              { id: response.data.brokerId },
              {},
              {
                headers: {
                  Authorization: accessToken,
                },
              },
            )
          })
          .then((brokerResponse) => {
            setExistingSubDetail(false)
            setMappedSubmission({
              primaryInsuredName: response.data.primaryInsuredName,
              agencyName: brokerResponse.data.data.name,
              createdAt: moment(response.data.createdAt).format('D/MM/YYYY'),
            })
          })
          .catch((error) => {
            console.error(error)
          })
        setExistingSubDetail(true)
        setAlreadyUsedNumber(true)
        setSaveLoading(false)
        return
      }
      updateEdgeNumber(submission, edgeNumber)
      if (typeof edgeNumber === 'string' && edgeNumber.length > 0) {
        setSaveSuccess(true)
      }
      return
    } catch (error) {
      console.log(error)
      setNoSuchSubmission(true)
    }
    setSaveLoading(false)
  }

  const getSubmissionForEdgeNumber = async (number) => {
    return client.query({
      fetchPolicy: 'no-cache',
      variables: {
        edgeNumber: number,
      },
      query: gql`
        query submissionForEdgeNumber($edgeNumber: String) {
          submissionForEdgeNumber(edgeNumber: $edgeNumber) {
            id
          }
        }
      `,
    })
  }

  const handleCheckboxChange = async (event) => {
    setMapCheckLoader(true)
    setInvalidNumber(false)
    setAlreadyUsedNumber(false)
    setSaveSuccess(false)
    setInput('')
    setSubmissionUnlinked(false)

    if (event.target.checked) {
      setDoNotMapChecked(true)
      updateEdgeNumber(submission, 'N/A')
      setSubmissionUnlinked(true)
    } else {
      setDoNotMapChecked(false)
      updateEdgeNumber(submission, '')
    }
  }

  const product =
    submission?.type === 'oi' ? "Owner's Interest" : 'Owners and Contractors Protective'

  return (
    <div>
      {showLoading === true ? (
        <div className="modal-loader">
          <Loading width="44px" height="44px" />
        </div>
      ) : (
        <div className="modal-main">
          <div className="modal-details">
            <div className="details-heading">
              <label>Owner&apos;s Edge: {product} Submission</label>
            </div>
            <div className="row details-content">
              <div className="col-xs-12 col-md-6">
                <address>
                  <small className="text-muted">First Name Insured</small>
                  <br />
                  {submission?.primaryInsuredName}
                  <br />
                  {submission?.insuredAddress.primaryInsuredAddress}{submission?.insuredAddress.primaryInsuredAddress2 ? `${', '}${submission?.insuredAddress.primaryInsuredAddress2}` : ''}<br />
                  {submission?.insuredAddress.primaryInsuredCity}{', '}
                  {submission?.insuredAddress.primaryInsuredState}{' '}
                  {submission?.insuredAddress.primaryInsuredZipcode}
                  <br />
                </address>
                <address>
                  <small className="text-muted">Project Address</small>
                  <br />
                  {submission?.projectAddress.projectAddress}{submission?.projectAddress.projectAddress2 ? `${', '}${submission?.projectAddress.projectAddress2}` : ''}
                  <br />
                  {submission?.projectAddress.projectCity}{', '}
                  {submission?.projectAddress.projectState}{' '}
                  {submission?.projectAddress.projectZipcode}
                  <br />
                </address>
              </div>
              <div className="col-xs-12 col-md-6 card-box">
                <small className="text-muted">Broker</small>
                <p>
                  {submission?.contactInfo.email} <br />
                </p>

                <small className="text-muted">Agency</small>
                <p>{broker.name}</p>
                <small>
                  <p className="text-muted">
                    {moment(submission?.createdAt).format('D/MM/YYYY')} submitted <br />
                  </p>
                </small>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-xs-12 col-md-4">
              <form>
                EDGE Submission Number
                <input
                  type="text"
                  className="text-input"
                  onChange={handleChange}
                  value={input}
                  disabled={doNotMapChecked}
                  inputMode='numeric'
                />
                <button
                  className="btn-default btn save-button"
                  onClick={handleSave}
                  disabled={saveLoading || doNotMapChecked}
                >
                  Save Changes
                </button>
              </form>
            </div>
            {alreadyUsedNumber && (
              <div className="col-xs-12 col-md-6">
                <small className="text-muted">
                  EDGE Number already linked to another submission:
                </small>
                {existingSubDetail && (
                  <div className="modal-loader col-xs-12 col-md-6">
                    <Loading width="10px" height="10px" />
                  </div>
                )}
                <br />
                <p>
                  <strong>{mappedSubmission.primaryInsuredName}</strong> <br />
                  {mappedSubmission.agencyName} <br />
                  {mappedSubmission.createdAt} <br />
                </p>
              </div>
            )}
          </div>
          <div className="notification-text">
            <div className="row">
              <div className="col-xs-12 col-md-6">
                {invalidNumber && (
                  <div className="warning-text">
                    Submission number cannot exceed 15 characters
                  </div>
                )}
                {saveSuccess && (
                  <div className="success-text">Submission linked to EDGE</div>
                )}
                {submissionUnlinked && (
                  <div className="success-text">Submission is unlinked from EDGE</div>
                )}
                {alreadyUsedNumber && (
                  <div className="error-text">
                    Number already linked to another submission
                  </div>
                )}
                {updateEdgeNumberFailure && (
                  <div className="error-text">
                    Error encountered
                  </div>
                )}
                {(saveLoading || mapCheckLoader) && (
                  <div>
                    <Loading width="22px" height="22px" />
                  </div>
                )}
                {noSuchSubmission && (
                  <div className="error-text">
                    The entered Edge submission number does not exist
                  </div>
                )}
              </div>
            </div>
          </div>
          <div>
            <div className="row">
              <div className="col-xs-12 col-md-3">
                <Checkbox
                  inline
                  onClick={handleCheckboxChange}
                  id="map-checkbox"
                  checked={doNotMapChecked}
                >
                  Do not map
                </Checkbox>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const mapStateToProps = (state) => ({
  user: state.user,
  person: state.person,
  updateEdgeNumberIsBusy: state.app.updateEdgeNumberIsBusy,
  updateEdgeNumberSuccess: state.app.updateEdgeNumberSuccess,
  updateEdgeNumberFailure: state.app.updateEdgeNumberFailure
})

export default connect(mapStateToProps, {
  updateSubmissionWithEdgeNumber,
  updateSubmissionWithEdgeNumberReset
})(LinkEdgeNumberModal)
